import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useVenue } from "../lib/venue";
import { resolveRadioContent, type RadioContent } from "../lib/radio-content";
import { useNowPlaying, type LiveNow } from "../lib/use-now-playing";
import { useNowPlayingTrack } from "../lib/use-now-playing-track";
import { attachStream } from "../lib/attach-stream";
import type { ScheduleEntry } from "../lib/radio-schedule";
import type { LiveTrack } from "../lib/radio-now-playing";
import "../radio.css";

const NO_ENTRIES: ScheduleEntry[] = [];

// ── Shared radio UI: the SWU dot-cluster mark, icons, and a player context so
// every radio page shares one <audio> element + play state (persists across
// route changes because the shell doesn't remount on navigation). ──

type RadioCtx = {
  content: RadioContent;
  playing: boolean;
  toggle: () => void;
  listeners: number;
  now: LiveNow;
  liveTrack: LiveTrack | null;
};
const Ctx = createContext<RadioCtx | null>(null);
export function useRadio(): RadioCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useRadio must be used within RadioShell");
  return c;
}

export function MarkDefs() {
  return (
    <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
      <symbol id="swu-mark" viewBox="0 0 104 92">
        <circle className="d" cx="28" cy="13" r="10" />
        <circle className="d" cx="16" cy="35" r="10" />
        <circle className="d" cx="40" cy="35" r="10" />
        <circle className="d" cx="64" cy="35" r="10" />
        <circle className="d" cx="88" cy="35" r="10" />
        <circle className="d" cx="28" cy="57" r="10" />
        <circle className="d" cx="52" cy="57" r="10" />
        <circle className="d" cx="76" cy="57" r="10" />
        <circle className="d" cx="16" cy="79" r="10" />
      </symbol>
    </svg>
  );
}
export function Mark({ className }: { className?: string }) {
  return (
    <svg className={className ? `logo ${className}` : "logo"} viewBox="0 0 104 92" width="30">
      <use href="#swu-mark" />
    </svg>
  );
}
export const PlayIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M8 5v14l11-7z" />
  </svg>
);
export const PauseIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
  </svg>
);

// Tab-bar icons (mobile).
const IcHome = () => <svg viewBox="0 0 24 24"><path d="M12 3l9 8h-3v9h-4v-6H10v6H6v-9H3z" /></svg>;
const IcCal = () => <svg viewBox="0 0 24 24"><path d="M7 2v2H5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2V2h-2v2H9V2zm12 8v9H5v-9z" /></svg>;
const IcGrid = () => <svg viewBox="0 0 24 24"><path d="M3 3h8v8H3zm10 0h8v8h-8zM3 13h8v8H3zm10 0h8v8h-8z" /></svg>;
const IcBag = () => <svg viewBox="0 0 24 24"><path d="M6 7V6a6 6 0 1 1 12 0v1h3l-1 15H4L3 7zm2 0h8V6a4 4 0 1 0-8 0z" /></svg>;
const IcHeart = () => <svg viewBox="0 0 24 24"><path d="M12 21s-8-5-8-11a4.5 4.5 0 0 1 8-2.8A4.5 4.5 0 0 1 20 10c0 6-8 11-8 11z" /></svg>;

const NAV: { to: string; label: string; Icon: () => ReactNode }[] = [
  { to: "/", label: "Live", Icon: IcHome },
  { to: "/schedule", label: "Schedule", Icon: IcCal },
  { to: "/shows", label: "Shows", Icon: IcGrid },
  { to: "/shop", label: "Shop", Icon: IcBag },
  { to: "/support", label: "Support", Icon: IcHeart },
];

export function RadioShell({ children }: { children: ReactNode }) {
  const { data } = useVenue();
  const content = resolveRadioContent(data?.venue);

  const [playing, setPlaying] = useState(false);
  const [listeners, setListeners] = useState(1284);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const now = useNowPlaying(content?.schedule ?? NO_ENTRIES, content?.timezone);
  const liveTrack = useNowPlayingTrack(content?.nowPlayingUrl);

  useEffect(() => {
    const id = setInterval(() => {
      setListeners((n) => Math.max(1120, Math.min(1620, n + Math.round((Math.random() - 0.45) * 12))));
    }, 2800);
    return () => clearInterval(id);
  }, []);

  // Attach the live stream (Icecast direct, or HLS via hls.js) when the URL is set.
  useEffect(() => {
    const audio = audioRef.current;
    const url = content?.streamUrl;
    if (!audio || !url) return;
    let cleanup = () => {};
    let cancelled = false;
    void attachStream(audio, url).then((c) => {
      if (cancelled) c();
      else cleanup = c;
    });
    return () => {
      cancelled = true;
      cleanup();
    };
  }, [content?.streamUrl]);

  if (!content) return <>{children}</>;

  // On-air label from the live clock, falling back to the seed's `live` or "Off air".
  const onAirName = now.current?.show ?? content.live?.name ?? "Off air";
  const onAirSub = now.current?.host ?? content.live?.with ?? "";

  function toggle() {
    const audio = audioRef.current;
    const next = !playing;
    setPlaying(next);
    if (audio && content?.streamUrl) {
      if (next) void audio.play().catch(() => undefined);
      else audio.pause();
    }
  }

  const suffix = content.suffix ?? "";

  return (
    <Ctx.Provider value={{ content, playing, toggle, listeners, now, liveTrack }}>
      <div className="swu-radio">
        <MarkDefs />
        {content.streamUrl ? <audio ref={audioRef} preload="none" /> : null}

        {/* header */}
        <header className="rhead">
          <div className="wrap bar">
            <Link className="brand" to="/">
              <Mark />
              <span className="wm">
                {content.wordmark}
                <span>{suffix}</span>
              </span>
            </Link>
            <nav className="rnav">
              {NAV.filter((n) => n.to !== "/").map((n) => (
                <Link key={n.to} to={n.to} activeProps={{ className: "on" }}>
                  {n.label}
                </Link>
              ))}
            </nav>
            <div className="live-pill">
              <span className="txt">
                <span className="live-dot breathe" /> <b>{onAirName}</b>
                {onAirSub ? ` · ${onAirSub}` : ""}
              </span>
              <button className="pbtn" onClick={toggle} aria-label={playing ? "Pause" : "Play live stream"}>
                {playing ? <PauseIcon /> : <PlayIcon />}
              </button>
            </div>
          </div>
        </header>

        {/* page content */}
        <main className="rmain">{children}</main>

        {/* footer */}
        <footer className="rfoot">
          <div className="wrap fg">
            <div>
              <Mark />
              <p>
                Independent underground radio, broadcasting bass music 24/7
                {content.location ? ` from ${content.location.split(",")[0]}` : ""}.
              </p>
            </div>
            <div className="fc">
              <h4>Tune in</h4>
              <Link to="/">Listen live</Link>
              <Link to="/schedule">Schedule</Link>
              <Link to="/shows">Shows</Link>
              <Link to="/support">Support</Link>
            </div>
            <div className="fc">
              <h4>Follow</h4>
              {content.socials.map((s, i) => (
                <a key={i} href={s.href} target="_blank" rel="noreferrer">
                  {s.label}
                </a>
              ))}
            </div>
          </div>
          <div className="wrap fb">
            <span className="lbl">
              © 2026 {content.wordmark}
              {suffix}
              {content.location ? ` · ${content.location}` : ""}
            </span>
            <span className="built">
              Built on <b>Assembly</b>
            </span>
          </div>
        </footer>

        {/* mobile: sticky mini-player + tab bar */}
        <div className="rmini">
          <span className="live-dot breathe" />
          <span className="mtxt">
            <b>{onAirName}</b>
            {onAirSub ? ` · ${onAirSub}` : ""}
          </span>
          <button className="pbtn" onClick={toggle} aria-label={playing ? "Pause" : "Play live stream"}>
            {playing ? <PauseIcon /> : <PlayIcon />}
          </button>
        </div>
        <nav className="rtabs">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} activeOptions={{ exact: n.to === "/" }} activeProps={{ className: "on" }}>
              <n.Icon />
              <span>{n.label}</span>
            </Link>
          ))}
        </nav>
      </div>
    </Ctx.Provider>
  );
}
